import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { DataTable } from "@/components/shared/DataTable";
import { VariantOptionForm } from "@/components/variant-options/VariantOptionForm";
import { FormDialog } from "@/components/shared/FormDialog";
import { VariantOptionsFilters } from "@/components/variant-options/VariantOptionsFilters";
import { variantOptionColumns } from "@/components/variant-options/VariantOptionColumns";
import { useVariantOptions } from "@/hooks/useVariantOptions";
import { useFactories } from "@/hooks/useFactories";
import { useVariants } from "@/hooks/useVariants";

export function VariantOptionsManager() {
  const { variantOptions, loading, createVariantOption, updateVariantOption, deleteVariantOption } = useVariantOptions();
  const { factories, loading: loadingFactories } = useFactories();
  const { variants, loading: loadingVariants } = useVariants();
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [selectedVariantOption, setSelectedVariantOption] = useState<any>(null);

  const [filters, setFilters] = useState({
    factory: "",
    variant: "",
    optionName: "",
    optionValue: "",
    priceChange: ""
  });

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      factory: "",
      variant: "",
      optionName: "",
      optionValue: "",
      priceChange: ""
    });
  };

  const filteredVariantOptions = useMemo(() => {
    return variantOptions.filter(option => {
      // Factory filter
      if (filters.factory) {
        const optionFactories = Array.isArray(option.factories) ? option.factories : [];
        const hasFactory = optionFactories.some((factoryId: string) => 
          factoryId === filters.factory || factoryId?.toString() === filters.factory?.toString()
        );
        if (!hasFactory) {
          return false;
        }
      }

      // Variant filter
      if (filters.variant) {
        const optionVariants = Array.isArray(option.variant) ? option.variant : [];
        const hasVariant = optionVariants.some((variantId: string) => 
          variantId === filters.variant || variantId?.toString() === filters.variant?.toString()
        );
        if (!hasVariant) {
          return false;
        }
      }

      // Option name filter
      if (filters.optionName && !option.option_name?.toLowerCase().includes(filters.optionName.toLowerCase())) {
        return false;
      }

      // Option value filter
      if (filters.optionValue && !option.option_value?.toLowerCase().includes(filters.optionValue.toLowerCase())) {
        return false;
      }

      // Price change filter
      if (filters.priceChange) {
        const priceChange = Number(option.price_change || 0);
        switch (filters.priceChange) {
          case "positive":
            if (priceChange <= 0) return false;
            break;
          case "negative":
            if (priceChange >= 0) return false;
            break;
          case "zero":
            if (priceChange !== 0) return false;
            break;
        }
      }

      return true;
    });
  }, [variantOptions, filters]);

  const handleDelete = async (variantOption: any) => {
    if (confirm(`Are you sure you want to delete the option "${variantOption.option_name}: ${variantOption.option_value}"?`)) {
      await deleteVariantOption(variantOption);
      return true;
    }
    return false;
  };

  const handleAdd = () => {
    setSelectedVariantOption(null);
    setIsFormDialogOpen(true);
  };

  const handleEdit = (variantOption: any) => {
    setSelectedVariantOption(variantOption);
    setIsFormDialogOpen(true);
  };

  const handleFormSubmit = async (values: any) => {
    const result = selectedVariantOption
      ? await updateVariantOption(selectedVariantOption.id, values)
      : await createVariantOption(values);

    if (result) {
      setIsFormDialogOpen(false);
      return true;
    }
    return false;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold tracking-tight">Variant Options</h2>
        <Button onClick={handleAdd} className="flex items-center gap-1">
          <Plus className="h-4 w-4" /> Add Option
        </Button>
      </div>

      <VariantOptionsFilters
        factories={factories}
        variants={variants}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        loading={loading || loadingFactories || loadingVariants}
      />

      <DataTable
        columns={variantOptionColumns}
        data={filteredVariantOptions}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={handleAdd}
        title="Variant Options"
        itemName="Variant Option"
        isLoading={loading}
        searchColumns={["variant_names"]}
      />

      <FormDialog
        open={isFormDialogOpen}
        onOpenChange={setIsFormDialogOpen}
        title={selectedVariantOption ? "Edit Variant Option" : "Add New Variant Option"}
      >
        <VariantOptionForm
          initialData={selectedVariantOption}
          onSubmit={handleFormSubmit}
          onCancel={() => setIsFormDialogOpen(false)}
        />
      </FormDialog>
    </div>
  );
}
